import type { FixedTermMarket } from '@/utils/internalAppSdk'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { APP_URL, SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { formatDecimalToPercentage, formatUSD } from '@vaultsfyi/common/utils'
import { getMetaTags } from './getMetaTags'

interface Props {
  market: FixedTermMarket | undefined
  pathname: string
}

const propertyValue = (propertyID: string, value: string | number, name?: string) => ({
  '@type': 'PropertyValue',
  propertyID,
  value,
  ...(name && { name }),
})

export const getFixedTermMarketMeta = ({ market, pathname }: Props) => {
  if (!market) {
    return getMetaTags({
      title: 'Fixed-term market',
      description: 'View fixed-term market details.',
      url: pathname,
    })
  }

  const networkName = getNetworkName(market.network.name)
  const network = networkName.charAt(0).toUpperCase() + networkName.slice(1)
  const protocolName = getProtocolDisplayName(market.protocol)
  const fixedApy = formatDecimalToPercentage(market.fixedApy, { notation: 'standard' })
  const maturity = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(market.expiry * SECOND_IN_MILLISECONDS)
  const title = `${market.name} by ${protocolName} on ${network}`
  const description = `${market.name} is a ${protocolName} fixed-term market on ${network} offering ${fixedApy} fixed APY until ${maturity}. TVL: ${formatUSD(market.tvl.usd)}. PT contract: ${market.pt.address}.`
  const pageUrl = `${APP_URL}${pathname}`

  return getMetaTags({
    title,
    description,
    url: pathname,
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'FinancialProduct',
        '@id': pageUrl,
        name: market.name,
        url: pageUrl,
        description,
        provider: { '@type': 'Organization', name: protocolName },
        interestRate: Math.round(market.fixedApy * 10_000) / 100,
        identifier: [
          propertyValue('market', market.vaultId),
          propertyValue('network', market.network.networkCaip, network),
          propertyValue('principalToken', market.pt.address, market.pt.symbol),
          propertyValue('principalTokenCaip', market.pt.assetCaip),
          propertyValue('underlyingAsset', market.underlyingAsset.address, market.underlyingAsset.symbol),
          propertyValue('underlyingAssetCaip', market.underlyingAsset.assetCaip),
        ],
        additionalProperty: [
          propertyValue('maturity', new Date(market.expiry * SECOND_IN_MILLISECONDS).toISOString()),
          propertyValue('tvlUsd', market.tvl.usd),
          propertyValue('liquidityUsd', market.liquidity.usd),
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'vaults.fyi', item: APP_URL },
          { '@type': 'ListItem', position: 2, name: 'Fixed term', item: `${APP_URL}/fixed-term` },
          { '@type': 'ListItem', position: 3, name: market.name, item: pageUrl },
        ],
      },
    ],
  })
}
